// Hot Works — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const HOT_WORKS: Record<string, TemplateSpecific> = {
  "hot-boiler-install": {
    whoFor:
      "This RAMS is for the heating engineer installing or replacing a gas or oil boiler — typically when a client, landlord or principal contractor wants the new appliance fitted, flued and commissioned with evidence it is safe to use. It suits the Gas Safe or OFTEC registered engineer working in an occupied house, plant room or new build who needs to show that the hot works on the pipework, the flue and the combustion checks are controlled and that the appliance is left safe and certificated.",
    scope:
      "The scope covers positioning the boiler, running and jointing the pipework, installing and testing the flue, connecting the gas or oil supply, and commissioning the appliance with a combustion analysis. It includes the hot works involved in jointing pipework and the working at height needed for the flue terminal. It does not cover the gas supply pipe upstream of the meter, oil tank installation, or electrical work beyond the appliance connection, which are covered by their own methods.",
    legislation: [
      "Gas Safety (Installation and Use) Regulations 1998",
      "Building Regulations Approved Document J (combustion appliances and flues)",
      "Construction (Design and Management) Regulations 2015",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002",
      "Regulatory Reform (Fire Safety) Order 2005 for hot works",
    ],
    sequence: [
      "Confirm the appliance, flue route and clearances against Approved Document J and the manufacturer's instructions before starting.",
      "Move the boiler into position using mechanical aid or a team lift and protect the surrounding finishes and occupants' access.",
      "Run the pipework and carry out brazing or soldering joints under hot-works controls, away from combustible building fabric.",
      "Install the flue, confirm the terminal position and clearances, and check the whole flue route is sealed and supported.",
      "Connect the gas or oil supply, carry out a tightness test and purge, and confirm there is no leak before firing the appliance.",
      "Commission the boiler, take a combustion analysis, check the flue for spillage, and leave the appliance certificated with the user instructed.",
    ],
    hazards: [
      {
        hazard: "Carbon monoxide exposure from an incorrectly installed, unsealed or unproven flue",
        who: ["Operatives", "Occupants"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Install the flue to the manufacturer's instructions and Approved Document J clearances and confirm every joint is sealed and supported",
          "Carry out a flue flow and spillage test and a combustion analysis at commissioning before handing over",
          "Do not leave the appliance in use until the flue is proven and the combustion readings are within limits",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Fire ignition of combustible building fabric while brazing or soldering pipework",
        who: ["Operatives", "Occupants"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Work under a hot-works permit with combustibles removed or protected by a fire blanket and heat mat",
          "Keep a suitable extinguisher at the point of work and maintain a fire watch during and after jointing",
          "Use press-fit connections in place of a naked flame where the location is enclosed or near insulation",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Gas or oil escape and explosive atmosphere during connection and commissioning",
        who: ["Operatives", "Occupants"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Carry out a tightness test and purge to the gas regulations before firing the appliance",
          "Eliminate ignition sources and ventilate the space while making and testing the supply connection",
          "Cap and make safe the supply, then evacuate and ventilate if an escape is found before any further work",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Musculoskeletal injury manoeuvring the boiler into a loft, airing cupboard or plant room",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Plan the route and use a sack truck, stair climber or team lift for the appliance",
          "Clear and light the access and use a loft ladder or tower rather than balancing the unit at height",
          "Split the appliance into its lighter sub-assemblies where the manufacturer allows",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Flame-resistant gloves and gauntlets for brazing",
      "Eye protection rated for hot works and pipe cutting",
      "Safety footwear with toe protection",
      "Knee pads and head protection for loft and cupboard installations",
    ],
    competence: [
      "Gas Safe registration for the relevant appliance category, or OFTEC registration for oil",
      "Current ACS assessments for the boiler and flue types being worked on",
      "Hot-works and fire-watch training",
      "Manual handling training for appliance moves",
    ],
    permits: [
      "Hot-works permit for the brazing and soldering operations",
      "Gas or oil supply isolation agreement before connection",
      "Working-at-height arrangement for the external flue terminal where applicable",
    ],
    equipment: [
      "Brazing or soldering kit with fire blanket, heat mat and extinguisher",
      "Flue flow and spillage test equipment",
      "Electronic combustion analyser and gas tightness gauge",
      "Press-fit tool where naked-flame jointing is to be avoided",
      "Sack truck or appliance stair climber for the boiler move",
    ],
    pcChecks: [
      "That the flue is installed to Approved Document J and proven by flow, spillage and combustion checks before hand-over",
      "That brazing and soldering are covered by a hot-works permit with a fire watch maintained after the flame is out",
      "That a tightness test and purge are completed before the appliance is fired, with the result recorded",
    ],
    faqs: [
      {
        q: "Does this RAMS need a hot-works permit if I am using press-fit fittings?",
        a: "If the whole job is genuinely press-fit with no naked flame, the fire-ignition hazard from brazing largely falls away and a hot-works permit may not be needed — but most boiler installs still involve some soldering or brazing on the tails or existing pipework. The method keeps the hot-works controls in because the risk depends on what you actually do on the day, not the headline jointing method. Where you can use press-fit in an enclosed or insulated location, that is the safer choice and the RAMS treats it as the preferred control rather than relying on the permit alone.",
      },
      {
        q: "Why is carbon monoxide treated as the highest-severity hazard here?",
        a: "A boiler that fires but vents combustion products into the room because of a poorly sealed or wrongly sited flue can put occupants at risk of carbon monoxide poisoning after you have left site. That is why the flue installation, flow and spillage testing and the commissioning combustion analysis are non-negotiable steps, and why the appliance must not be left in use until those checks pass. The danger is not visible during the install, so the controls rely on proving the flue and combustion rather than on it looking right.",
      },
    ],
  },
  "hot-welding": {
    whoFor:
      "This welding hot works RAMS is for mechanical and fabrication contractors carrying out arc or gas welding on site — pipework, structural steel, brackets and plant connections. If the principal contractor or client has asked for a hot works risk assessment before issuing you a permit, this is the document they expect to see, and it must account for the fact that welding fume is now treated as a carcinogen. It gives you the recognised hazards, fume controls and permit interface so the competent person signing it can confirm it matches the real job.",
    scope:
      "Manual metal arc (MMA), MIG/MAG and TIG welding of mild and stainless steel components, plus oxy-fuel gas welding where used, carried out within the agreed work area under a hot works permit. The scope covers fume extraction and respiratory protection, screening of adjacent operatives, management of welding sets and gas cylinders, and the fire watch on completion. It does not cover structural design, weld procedure qualification (WPS/PQR), or weld inspection and NDT, which are managed separately. Cutting and grinding of the same components is addressed under a separate task RAMS where it forms part of the works.",
    legislation: [
      "Health and Safety at Work etc. Act 1974",
      "Management of Health and Safety at Work Regulations 1999",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH)",
      "HSE welding fume enforcement position (Safety Alert, 2019) and WL3 guidance — all weld fume treated as carcinogenic",
      "Regulatory Reform (Fire Safety) Order 2005",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Electricity at Work Regulations 1989",
    ],
    sequence: [
      "Obtain the hot works permit, confirm the welding area, and remove or protect combustible materials within at least 10 metres; screen off the area to protect other operatives from arc flash.",
      "Set up local exhaust ventilation (LEV) at the weld point, position the extraction hood within capture distance, and confirm it is drawing before the arc is struck.",
      "Inspect the welding set, leads, return clamp and electrode holder for damage; confirm the 110V or correctly protected supply and that the return clamp is fixed close to the weld.",
      "Secure gas cylinders upright in a trolley or restraint, fit flashback arrestors and non-return valves on oxy-fuel sets, and leak-test connections with detection fluid.",
      "Don the welding mask, FFP3 or powered respirator and arc-rated clothing, then carry out welding with LEV running and the screen in place.",
      "On completion, isolate the welding set, close cylinder valves and bleed the lines, then keep the area under fire watch for the permit period before signing off.",
    ],
    hazards: [
      {
        hazard:
          "Inhalation of welding fume (now classified carcinogenic — includes mild steel; manganese and, on stainless, hexavalent chromium)",
        who: ["Welder", "Operatives working nearby"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use local exhaust ventilation (LEV) at the weld point for all welding, indoors or outdoors, in line with the 2019 HSE enforcement position",
          "Provide and face-fit RPE — minimum FFP3, or a powered air-purifying respirator for stainless/long-duration work",
          "Position the welder so the head is out of the fume plume and rotate tasks to limit exposure time",
          "Maintain and LEV-test the extraction equipment on a thorough examination schedule under COSHH",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Arc eye and ultraviolet skin burns from the welding arc",
        who: ["Welder", "Operatives working nearby"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Erect opaque welding screens around the arc to protect passers-by and adjacent trades",
          "Use a welding helmet with the correct shade to BS EN 175, with auto-darkening filters checked before use",
          "Cover exposed skin with arc-rated clothing, gauntlets and a flame-retardant collar",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Electric shock from the welding set, leads or damaged supply",
        who: ["Welder"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use welding sets fed through an RCD-protected supply and inspect leads, holders and the return clamp before each use",
          "Fix the return (earth) clamp directly to the workpiece close to the weld, never through the structure",
          "Keep gloves and clothing dry and stand on a dry insulated surface in damp or confined conditions",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard:
          "Fire or explosion from sparks, spatter and hot slag igniting combustibles",
        who: ["Welder", "Building occupants", "Other trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Clear or cover combustibles within 10m, use fire blankets and an attendant with an extinguisher to hand",
          "Work under a hot works permit with the fire watch maintained for the permit period after the last spark",
          "Confirm no flammable atmosphere is present (DSEAR check) before striking the arc in tanks, voids or near services",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Compressed gas cylinders (oxygen/acetylene) — leaks, flashback or cylinder fall during manual handling",
        who: ["Welder", "Operatives moving cylinders"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Move full and empty cylinders on a purpose-built cylinder trolley, never carried loose by hand or rolled",
          "Secure cylinders upright and restrained when in use and when stored, valves uppermost",
          "Fit flashback arrestors and non-return valves, leak-test joints, and store oxygen and fuel gas apart and away from heat",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Welding helmet to BS EN 175 with correct-shade or auto-darkening filter",
      "FFP3 respirator or powered air-purifying respirator (face-fitted)",
      "Arc-rated/flame-retardant jacket, gauntlets and leather apron",
      "Safety boots and flame-retardant overalls",
      "Hearing protection where grinding or chipping accompanies the work",
    ],
    competence: [
      "Coded/qualified welder competent in the relevant process (MMA, MIG/MAG, TIG or oxy-fuel)",
      "Current face-fit test for the RPE being worn",
      "Hot works permit awareness and fire watch duties",
      "COSHH and welding fume awareness training",
    ],
    permits: [
      "Hot works permit covering the welding area and fire watch period",
      "Confined space permit where welding inside tanks, vessels or voids",
      "Permit to work / isolation certificate for live plant or services nearby",
    ],
    equipment: [
      "Welding set (MMA inverter, MIG/MAG or TIG plant) with RCD-protected supply",
      "Local exhaust ventilation unit with capture hood or on-torch extraction",
      "Oxy-fuel set with regulators, flashback arrestors and non-return valves",
      "Cylinder trolley with restraint straps",
      "Welding screens, fire blankets and a CO2 or dry powder extinguisher",
    ],
    pcChecks: [
      "LEV is specified for ALL welding including mild steel, with RPE as a supplement — not RPE on its own (the post-2019 HSE position)",
      "Current LEV thorough examination/test records and welder face-fit certificates are referenced or attached",
      "The fire watch duration and who holds it are stated, and combustibles within 10m have a clearance or protection measure",
    ],
    faqs: [
      {
        q: "Do I need LEV for welding mild steel, or is a mask enough?",
        a: "You need LEV. Since the HSE changed its enforcement position in February 2019, all welding fume — including mild steel — is treated as carcinogenic, so general ventilation alone is no longer acceptable. The expectation is engineering control first: local exhaust ventilation at the weld point, indoors or outdoors. RPE such as an FFP3 or powered respirator is required on top of LEV, not instead of it, and only works where the wearer has been face-fit tested. A RAMS that relies on a dust mask alone will be rejected by most principal contractors.",
      },
      {
        q: "How long does the fire watch need to run after I finish welding?",
        a: "The fire watch runs for the period set on the hot works permit, which is usually a minimum of 60 minutes after the last spark. Many clients and their insurers require longer — often a full hour of constant watch followed by periodic checks for up to an hour more. The point is that smouldering can take time to develop into a visible fire, especially where hidden combustibles or voids are nearby. Your RAMS should state the duration, name who holds the watch, and confirm extinguishers are kept to hand throughout.",
      },
    ],
  },

  "hot-cutting-grinding": {
    whoFor:
      "Written for general trades and labourers using angle grinders and cut-off saws on site — cutting steel, rebar, paving, blockwork and kerbs, and grinding welds or surfaces flush. A grinding risk assessment is one of the most commonly requested hot works documents because abrasive wheels and travelling sparks cause a high proportion of site injuries and fires. This RAMS sets out the wheel, dust, vibration and fire controls so the competent person can confirm the operator is trained under PUWER before the work starts.",
    scope:
      "Use of hand-held angle grinders, cut-off saws and bench/chop saws fitted with abrasive or diamond wheels to cut and grind metal, masonry and concrete within the agreed work area. The scope covers correct wheel selection and mounting, guarding, respiratory protection for respirable crystalline silica, hand-arm vibration limits, and fire precautions for travelling sparks. It excludes the welding and brazing operations that may feed the same job, which are covered by a separate hot works RAMS. Disc changing and PAT testing of the tools are carried out by competent persons outside the cutting task itself.",
    legislation: [
      "Health and Safety at Work etc. Act 1974",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER) — abrasive wheels, per HSE guidance HSG17",
      "Control of Vibration at Work Regulations 2005 (HAVS)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH) — respirable crystalline silica",
      "Regulatory Reform (Fire Safety) Order 2005",
      "Personal Protective Equipment at Work Regulations 1992 (as amended)",
    ],
    sequence: [
      "Confirm the operator is trained in mounting and changing abrasive wheels under PUWER, and check the wheel is correct for the material, undamaged and in date.",
      "Mount the wheel with the correct flanges and blotters, fit and adjust the guard, and ring-test/run-up the tool at a safe distance before applying it to the work.",
      "Survey the spark path — clear or screen combustibles within at least 10 metres, as sparks travel well beyond the immediate area, and obtain the hot works permit.",
      "Set up on-tool dust extraction or water suppression when cutting masonry or concrete to control respirable crystalline silica, and fit the correct RPE.",
      "Cut or grind using two hands, letting the wheel do the work, keeping the body out of the line of any kickback and not exceeding the tool's HAVS trigger time.",
      "On finishing, allow the wheel to stop before setting the tool down, clear hot offcuts, and keep the area under fire watch for the permit period.",
    ],
    hazards: [
      {
        hazard:
          "Abrasive wheel bursting or shattering, ejecting fragments at high speed",
        who: ["Operative", "Anyone in the line of fire"],
        initial: { likelihood: 2, severity: 4 },
        controls: [
          "Only abrasive-wheel-trained operatives (PUWER/HSG17) select, mount and change wheels using the correct flanges and blotters",
          "Match the wheel's maximum rated speed (rpm) to the tool and never exceed it; reject chipped, cracked or out-of-date wheels",
          "Keep the wheel guard fitted and correctly positioned, and stand out of the plane of rotation when starting up",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
      {
        hazard:
          "Kickback or loss of control of the grinder causing lacerations",
        who: ["Operative"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use both hands on the tool with the side handle fitted, and secure the workpiece in a vice or clamp where possible",
          "Cut on the correct side of the disc, let the wheel reach speed before contact and avoid pinching or twisting in the cut",
          "Allow the wheel to stop fully before putting the tool down and use anti-vibration cut-resistant gloves",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Respirable crystalline silica (RCS) dust when cutting masonry, paving or concrete",
        who: ["Operative", "Operatives working nearby"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Use on-tool dust extraction (M/H-class) or wet cutting/water suppression to stop dust at source — never dry-cut masonry uncontrolled",
          "Provide and face-fit FFP3 RPE for the operative and any close-up helpers",
          "Restrict the cutting area, keep others out of the dust cloud and damp down/vacuum debris rather than sweeping dry",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Hand-arm vibration syndrome (HAVS) from prolonged grinder/saw use",
        who: ["Operative"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Plan the work to stay within the daily exposure trigger time for the specific tool, sharing the task between operatives",
          "Use low-vibration tools and sharp/correct wheels so cutting is quick, and keep a firm but light grip",
          "Run health surveillance and let operatives take breaks to keep hands warm and maintain circulation",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard:
          "Fire from travelling sparks reaching combustibles 10m+ away or into voids",
        who: ["Operative", "Other trades", "Building occupants"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Direct sparks away from people and combustibles and use fire blankets/spark screens to contain the spark stream",
          "Clear or protect combustibles for at least 10m and check gaps, drains and voids the sparks could enter, under a hot works permit",
          "Keep an extinguisher to hand and maintain the fire watch for the permit period after cutting stops",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Impact goggles or face shield to BS EN 166",
      "FFP3 respirator (face-fitted) for masonry/concrete cutting",
      "Cut-resistant, anti-vibration gloves",
      "Hearing protection (cutting and grinding exceed exposure limits)",
      "Flame-retardant overalls and safety boots",
    ],
    competence: [
      "Abrasive wheels training (mounting, changing and using wheels) under PUWER/HSG17",
      "HAVS awareness and inclusion in health surveillance",
      "Current face-fit test for the RPE worn",
      "Hot works permit and fire watch awareness",
    ],
    permits: [
      "Hot works permit covering cutting/grinding and the fire watch",
      "Permit to dig or service-clearance check before cutting into floors or near buried services",
    ],
    equipment: [
      "Angle grinder and petrol/electric cut-off saw with rated abrasive or diamond wheels",
      "On-tool M/H-class dust extraction unit or water suppression kit",
      "Wheel guards, side handles and correct mounting flanges",
      "Spark screens and fire blankets",
      "CO2 or dry powder extinguisher",
    ],
    pcChecks: [
      "The operative named on the RAMS holds current abrasive wheels training and the wheel rpm rating is matched to the tool",
      "Dust control is at-source (on-tool extraction or wet cutting) for any masonry/concrete, not RPE alone",
      "HAVS trigger times are stated for the specific tools and the spark-path clearance distance is given for the fire risk",
    ],
    faqs: [
      {
        q: "Do operatives need a ticket to change an abrasive wheel?",
        a: "There is no single legal 'ticket', but under PUWER anyone who mounts or changes an abrasive wheel must be trained and competent to do so, and HSE guidance HSG17 sets out what that training covers. In practice this is delivered as an abrasive wheels course, and principal contractors will ask to see the certificate before the work starts. The training covers wheel selection, inspection, correct flanges and blotters, guarding and safe speeds. Naming an untrained operative on the RAMS is a common reason for it being sent back.",
      },
      {
        q: "Can I dry-cut paving slabs if I wear a dust mask?",
        a: "No — dry-cutting masonry, paving or concrete releases respirable crystalline silica at levels a disposable mask cannot control on its own. COSHH requires you to control the dust at source first, using on-tool extraction or water suppression, and only then add RPE such as a face-fitted FFP3 as a supplement. Uncontrolled dry-cutting is one of the enforcement priorities HSE inspects for on site. Your grinding risk assessment should specify the suppression method, not rely on a mask.",
      },
    ],
  },

  "hot-roofing-torch": {
    whoFor:
      "Roofing contractors applying torch-on felt and bitumen membranes with an LPG gas torch need this RAMS, and the principal contractor or building owner will expect to see it before granting a hot works permit. Torch-on roofing combines an open flame, LPG cylinders and work at height directly over an often combustible deck, which is why a torch on roofing method statement is one of the most closely scrutinised hot works documents. This document sets out the Safe2Torch controls, cylinder management and the extended fire watch so the competent person can confirm torch-free zones are designed in where they are needed.",
    scope:
      "Application of torch-applied reinforced bitumen membrane (underlay and cap sheet) to flat and low-pitch roofs using a hand-held or roofing LPG torch, including detailing at upstands, outlets and edges. The scope covers LPG cylinder handling at ground level and on the roof, Safe2Torch zoning around combustible details, fire watch and deck temperature checks, and the work-at-height interface with edge protection. It excludes the structural deck and insulation design, and any cold-applied or self-adhesive alternative system, which would change the hot works assessment. Where Safe2Torch identifies a torch-free detail, that detail is formed with a hot-air or cold-applied method instead of an open flame.",
    legislation: [
      "Health and Safety at Work etc. Act 1974",
      "Regulatory Reform (Fire Safety) Order 2005",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002 (DSEAR) — LPG",
      "Work at Height Regulations 2005",
      "NFRC Safe2Torch guidance for torch-on roofing",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
    ],
    sequence: [
      "Carry out the Safe2Torch survey against the drawings, mark the combustible details and edges, and confirm where torch-free (hot-air or cold-applied) detailing is required.",
      "Set up edge protection and access to the roof, then position the day's LPG cylinders in a ventilated, restrained location with spares stored separately at ground level.",
      "Inspect the torch, hose and regulator for damage, fit the correct regulator, and leak-test all connections with detection fluid before lighting.",
      "Prime and apply the membrane away from torch-free zones, keeping the flame moving and never directing it into a void, eaves, verge or up against timber.",
      "Form the marked torch-free details using hot-air welding or cold-applied bonding so no open flame contacts combustible elements.",
      "At the end of each torching session, close cylinder valves, return cylinders to the ground store, and start the post-work fire watch including temperature/thermal-imaging checks of the deck.",
      "Maintain the fire watch for the permit period — typically 60 minutes minimum, often 2 hours where the insurer requires it — before leaving the roof.",
    ],
    hazards: [
      {
        hazard:
          "Fire spread into the roof deck, voids, eaves or the building below from the open flame",
        who: ["Roofers", "Building occupants", "Adjacent property"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Apply Safe2Torch — design torch-free zones at all combustible details and form them with hot-air or cold-applied methods instead of a flame",
          "Keep the flame moving, never torch into voids, verges or against timber, and check the underside/void where access allows",
          "Work under a hot works permit and run a post-work fire watch with deck temperature/thermal-imaging checks before leaving",
          "Keep extinguishers and a water source on the roof within immediate reach throughout torching",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "LPG cylinder leak, flashback or fire on the roof or at the ground store",
        who: ["Roofers", "Other site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Keep only the cylinders needed for the session on the roof, upright and restrained, with spares stored at ground level in a ventilated cage away from ignition (LPG cylinder good practice / CP7)",
          "Leak-test the torch, hose and regulator with detection fluid before each shift and fit a hose check valve",
          "Turn cylinders off at the valve, not just the torch, during breaks and at the end of each session, and bleed the hose",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard:
          "Fall from the roof edge or through a fragile rooflight while torching",
        who: ["Roofers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Fit edge protection or a working platform to all open edges before any torching begins, under Work at Height Regs",
          "Cover, guard or mark fragile rooflights and openings, and keep hoses routed clear of trip and edge hazards",
          "Plan cylinder and material movement so operatives are not carrying loads near unprotected edges",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard:
          "Burns from the torch flame, hot bitumen and heated membrane",
        who: ["Roofers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Wear heat-resistant gauntlets, long sleeves and boots, and never pass a lit torch between operatives",
          "Rest the lit torch only on a stable stand with the flame directed away from people and the membrane roll",
          "Allow hot offcuts and bitumen to cool before handling and keep a burns kit on the roof",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard:
          "Smouldering missed during fire watch developing into a fire after the crew leaves",
        who: ["Building occupants", "Adjacent property"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Run the fire watch for the full permit period — 60 minutes minimum and up to 2 hours where the insurer requires it",
          "Use a surface thermometer or thermal imaging to confirm the deck and detailing have cooled, paying attention to edges and voids",
          "Record the fire watch sign-off on the permit and arrange a return inspection where any heat signature remains",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Heat-resistant roofing gauntlets",
      "Flame-retardant overalls with long sleeves",
      "Safety boots with heat-resistant soles",
      "Safety glasses to BS EN 166",
      "Harness and lanyard where collective edge protection is not practicable",
    ],
    competence: [
      "Trained and experienced torch-on / felt roofer competent in Safe2Torch",
      "LPG handling and torching awareness",
      "Hot works permit and fire watch duties",
      "Work at height and roof access training",
    ],
    permits: [
      "Hot works permit with an extended fire watch for torch-on roofing",
      "Roof access / work at height permit where required by the site",
    ],
    equipment: [
      "LPG roofing torch with regulator, hose and hose check valve",
      "LPG cylinders with a ventilated, restrained ground-level store and cage",
      "Reinforced bitumen membrane rolls and primer",
      "Hot-air welder or cold-applied bonding kit for torch-free details",
      "Surface thermometer or thermal imaging camera and roof extinguishers",
    ],
    pcChecks: [
      "Safe2Torch zoning is shown on the drawings — where torch-free details are required near combustible deck edges, eaves and rooflights, the RAMS must show them and name the alternative method",
      "The fire watch duration matches the permit/insurer requirement (60 min minimum, often 2 hours) and a deck temperature/thermal check is specified",
      "LPG cylinder management is described for both the roof and the ground-level store, including quantities on the roof and restraint",
    ],
    faqs: [
      {
        q: "What is Safe2Torch and does my method statement have to follow it?",
        a: "Safe2Torch is NFRC guidance that aims to reduce roof fires caused by torch-on work by identifying details where a naked flame should not be used. It requires you to survey the roof, mark the combustible details — eaves, verges, upstands, rooflights and penetrations — and form those details with hot-air welding or cold-applied methods instead of a torch. While it is guidance rather than a regulation, principal contractors and roofing insurers now expect torch on roofing method statements to follow it, and many will not issue a permit otherwise. Your RAMS should reference Safe2Torch and show the torch-free zones on the drawings.",
      },
      {
        q: "How many LPG cylinders can I keep on the roof while torching?",
        a: "Keep the quantity on the roof to the minimum needed for the session — typically the in-use cylinder plus, where unavoidable, one spare, both upright and restrained. The bulk of your supply should stay in a ventilated, secure store at ground level, away from ignition sources and drains, following LPG good practice (CP7 covers LPG in mobile and similar applications). At the end of each session cylinders are turned off at the valve and returned to the ground store rather than left on the roof overnight. State the on-roof quantity and the ground storage arrangement in the RAMS so the reviewer can see the LPG is controlled.",
      },
    ],
  },
};
